import {memo, useContext, useState} from 'react'

import PropTypes from 'prop-types'

import {atomIconSizes} from '@s-ui/react-atom-icon'
import AtomInput from '@s-ui/react-atom-input'
import Context from '@s-ui/react-context'
import {FilterBasicLegacyMultiSelectionNested} from '@adv-mt/mt-filter-basic-legacy'
import FilterWrapperBasic, {filterWrapperBasicAllSelectionsId as allSelectionsId} from '@adv-mt/mt-filter-wrapper-basic'
import MultiSelect from '@adv-mt/mt-form-filter-multi-select'
import Select, {formSelectSizes} from '@adv-mt/mt-form-select'
import IconSearch from '@adv-mt/mt-svg-icons/lib/Search'

import {LEGACY_WRAPPER} from './config.js'
import {getProvincesNameById, useGetCurrentOptions} from './utils.js'

const FormProvince = ({
  aggregations,
  initialSearch = {},
  isBasicContent = false,
  isMultiSelect = false,
  onChange,
  overlayContentRef,
  placeholder,
  type,
  value = [],
  withLabel = true,
  withSearchBar = false,
  wrapper = LEGACY_WRAPPER
}) => {
  const {i18n, domain} = useContext(Context)
  const config = domain.get('config')
  const {DEFAULT_PROVINCE, FILTER_NAMES} = config.get('search')
  const {DEFAULT_FILTERS_SEARCH} = config.get('searchFilters')
  const TRACKING_FILTER_NAMES = config.get('TRACKING_FILTER_NAMES')
  const provinces = config.get('provinces')
  const provincesByCCAA = config.get('provincesByCCAA')
  const label = withLabel && i18n.t('COMMON.PLURALS.PROVINCE', isMultiSelect ? 2 : 1)

  const currentValue = value.length ? value : [DEFAULT_PROVINCE]

  const [singleCurrentValue] = currentValue
  const [searchByText, setSearchByText] = useState('')

  const currentPlaceholder = placeholder || i18n.t('FORM_PROVINCE_DEFAULT_VALUE')

  const FIRST_OPTION = {
    id: DEFAULT_PROVINCE,
    label: currentPlaceholder
  }

  const currentOptions = useGetCurrentOptions({aggregations, i18n, provincesByCCAA, searchByText})

  const options = [FIRST_OPTION, ...provinces]

  const _handleOnChange = (e, {value}) => {
    const provinceIds = Array.isArray(value) ? value : [value]
    const provinceInitialSearch = initialSearch[FILTER_NAMES.PROVINCE]

    const hasLastFilter = provinceInitialSearch?.every(provinceId => provinceIds.includes(provinceId))

    onChange(e, {
      field: FILTER_NAMES.PROVINCE,
      value: provinceIds,
      lastFilter: hasLastFilter && TRACKING_FILTER_NAMES[FILTER_NAMES.PROVINCE]
    })
  }

  const handleChange = ids => _handleOnChange(null, {value: ids})

  const handleChangeSearch = e => {
    const {value} = e.target
    setSearchByText(value)
  }

  const handleReset = ({filters}) => {
    const [filterName] = filters
    onChange(null, {field: filterName, value: DEFAULT_FILTERS_SEARCH[filterName]})
  }

  if (wrapper === LEGACY_WRAPPER) {
    if (isBasicContent && isMultiSelect) {
      return (
        <div className="mt-FormProvinceBasicContent">
          {withSearchBar && (
            <div className="mt-FormProvinceBasicContent-search">
              <AtomInput
                leftIcon={<IconSearch size={atomIconSizes.medium} />}
                onChange={handleChangeSearch}
                value={searchByText}
                autoFocus
              />
            </div>
          )}
          <FilterBasicLegacyMultiSelectionNested
            fieldFilter={FIRST_OPTION}
            onChange={handleChange}
            options={currentOptions}
            showAllSelectionsField={!searchByText}
            values={value}
          />
        </div>
      )
    }

    return isMultiSelect ? (
      <MultiSelect
        field={FILTER_NAMES.PROVINCE}
        initialSearch={initialSearch}
        label={label}
        onChange={_handleOnChange}
        options={provinces}
        placeholder={currentPlaceholder}
        value={value}
      />
    ) : (
      <Select
        field={FILTER_NAMES.PROVINCE}
        initialSearch={initialSearch}
        label={label}
        onChange={_handleOnChange}
        options={options}
        size={formSelectSizes.LARGE}
        value={singleCurrentValue}
      />
    )
  }

  const currentPlaceholderValue = withLabel
    ? i18n.t('COMMON_ALL_PROVINCES')
    : i18n.t('COMMON.PLURALS.PROVINCE', isMultiSelect ? 2 : 1)

  return (
    <FilterWrapperBasic
      filters={[
        {allSelectionsLabel: i18n.t('COMMON_ALL_PROVINCES'), name: FILTER_NAMES.PROVINCE, options: currentOptions, type}
      ]}
      group={`${FILTER_NAMES.PROVINCE}Group`}
      legend={i18n.t('COMMON.PLURALS.PROVINCE', 2)}
      onChange={({value}) => {
        _handleOnChange(null, {value: value.filter(id => id !== allSelectionsId)})
      }}
      onReset={handleReset}
      overlayContentRef={overlayContentRef}
      placeholder={
        value.length
          ? getProvincesNameById({availableProvinces: provincesByCCAA, selectedProvinces: value})
          : currentPlaceholderValue
      }
      value={{[FILTER_NAMES.PROVINCE]: value.length ? value : [allSelectionsId]}}
      wrapper={wrapper}
    />
  )
}

FormProvince.displayName = 'FormProvince'
FormProvince.propTypes = {
  aggregations: PropTypes.array,
  initialSearch: PropTypes.object,
  isBasicContent: PropTypes.bool,
  isMultiSelect: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  overlayContentRef: PropTypes.node,
  placeholder: PropTypes.string,
  type: PropTypes.string,
  value: PropTypes.arrayOf(PropTypes.number),
  withLabel: PropTypes.bool,
  withSearchBar: PropTypes.bool,
  wrapper: PropTypes.string
}
const FormMultiProvince = props => {
  return <FormProvince {...props} isMultiSelect />
}
FormMultiProvince.displayName = 'FormMultiProvince'

const FormMultiNestedProvinceBasicContent = props => (
  <FormProvince {...props} isBasicContent isMultiSelect withSearchBar />
)

FormMultiNestedProvinceBasicContent.displayName = 'FormMultiNestedProvinceBasicContent'

const MemoFormProvince = memo(FormProvince)
const MemoFormMultiProvince = memo(FormMultiProvince)
const MemoFormMultiNestedProvinceBasicContent = memo(FormMultiNestedProvinceBasicContent)

export {
  getProvincesNameById,
  MemoFormMultiProvince as FormMultiProvince,
  MemoFormProvince as FormProvince,
  MemoFormMultiNestedProvinceBasicContent as FormMultiNestedProvinceBasicContent
}

export default MemoFormProvince
